import React, { useState, useRef } from "react";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  Spinner,
  Input,
  FormGroup,
  Label,
  Alert
} from "reactstrap";
import { Icon } from "../../../../components/Component";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import { createBulkBlogTopics } from "../../../../api/blogs/topics";
import Papa from "papaparse";

const BulkImportTopicsModal = ({ isOpen, toggle, onSuccess }) => {
  const axios = useAxiosPrivate();
  const [loading, setLoading] = useState(false);
  const [importMethod, setImportMethod] = useState("text"); // 'text' or 'csv'
  const [textInput, setTextInput] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState("");
  const [parsedData, setParsedData] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const fileInputRef = useRef(null);

  const handleTextInputChange = (e) => {
    setTextInput(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'text/csv') {
      toast.error('Only CSV files are accepted.');
      return;
    }

    setCsvFile(file);
    setCsvFileName(file.name);

    // Parse CSV file
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data.map(item => ({
          name: item.name || item.Name || item.topic || item.Topic || ""
        })).filter(item => item.name.trim() !== "");

        setParsedData(data);
        setPreviewData(data.slice(0, 5)); // Show first 5 items as preview
      },
      error: () => {
        toast.error('Error parsing CSV file.');
      }
    });
  };

  const parseTextInput = () => {
    if (!textInput.trim()) {
      toast.error('Please enter at least one topic.');
      return false;
    }

    const lines = textInput.split('\n').filter(line => line.trim() !== "");
    const data = lines.map(line => ({ name: line.trim() }));
    
    setParsedData(data);
    setPreviewData(data.slice(0, 5)); // Show first 5 items as preview
    return true;
  };

  const handleImport = async () => {
    try {
      setLoading(true);
      
      let dataToImport = [];
      
      if (importMethod === 'text') {
        if (!parseTextInput()) {
          setLoading(false);
          return;
        }
        dataToImport = parsedData;
      } else {
        if (!parsedData.length) {
          toast.error('Please upload a valid CSV file with topic data.');
          setLoading(false);
          return;
        }
        dataToImport = parsedData;
      }
      
      if (!dataToImport.length) {
        toast.error('No valid data to import.');
        setLoading(false);
        return;
      }

      await createBulkBlogTopics(axios, dataToImport);
      toast.success("Topics imported successfully");
      onSuccess();
      toggle();
      resetForm();
    } catch (error) {
      toast.error(error.message || "An error occurred during import");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTextInput("");
    setCsvFile(null);
    setCsvFileName("");
    setParsedData([]);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} className="modal-dialog-centered" size="lg">
      <ModalBody>
        <a
          href="#cancel"
          onClick={(ev) => {
            ev.preventDefault();
            toggle();
          }}
          className="close"
        >
          <Icon name="cross-sm"></Icon>
        </a>
        <div className="p-2">
          <h5 className="title">Bulk Import Topics</h5>
          <div className="mt-4">
            <Form>
              <div className="mb-4">
                <div className="d-flex mb-3">
                  <div className="form-check me-4">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="importMethod"
                      id="importMethodText"
                      checked={importMethod === 'text'}
                      onChange={() => setImportMethod('text')}
                    />
                    <label className="form-check-label" htmlFor="importMethodText">
                      Text Input (One topic per line)
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="importMethod"
                      id="importMethodCsv"
                      checked={importMethod === 'csv'}
                      onChange={() => setImportMethod('csv')}
                    />
                    <label className="form-check-label" htmlFor="importMethodCsv">
                      CSV Upload
                    </label>
                  </div>
                </div>

                {importMethod === 'text' ? (
                  <FormGroup>
                    <Label for="textInput">Enter Topics (one per line)</Label>
                    <Input
                      type="textarea"
                      id="textInput"
                      value={textInput}
                      onChange={handleTextInputChange}
                      rows={10}
                      placeholder="Enter topics here, one per line"
                    />
                  </FormGroup>
                ) : (
                  <FormGroup>
                    <Label for="csvFile">Upload CSV File</Label>
                    <Input
                      type="file"
                      id="csvFile"
                      onChange={handleFileChange}
                      accept=".csv"
                      ref={fileInputRef}
                    />
                    <small className="form-text text-muted">
                      CSV should have a 'name' column containing the topics.
                    </small>
                    {csvFileName && (
                      <div className="mt-2">
                        <span className="text-muted">Selected file: {csvFileName}</span>
                      </div>
                    )}
                  </FormGroup>
                )}
              </div>

              {previewData.length > 0 && (
                <div className="mb-4">
                  <h6>Preview:</h6>
                  <Alert color="light">
                    <ul className="mb-0">
                      {previewData.map((item, index) => (
                        <li key={index}>{item.name}</li>
                      ))}
                    </ul>
                    {parsedData.length > previewData.length && (
                      <div className="text-muted mt-2">
                        ...and {parsedData.length - previewData.length} more items
                      </div>
                    )}
                  </Alert>
                </div>
              )}

              <div className="form-group mt-4 d-flex justify-content-end">
                <Button color="secondary" className="me-2" onClick={toggle} disabled={loading}>
                  Cancel
                </Button>
                <Button color="primary" onClick={handleImport} disabled={loading}>
                  {loading ? <Spinner size="sm" /> : "Import Topics"}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default BulkImportTopicsModal;
